import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const styles = {
  page: {
    padding: "16px",
    maxWidth: 520,
    margin: "0 auto",
  },
  backWrap: {
    marginBottom: 16,
  },
  backButton: {
    cursor: "pointer",
    fontSize: "20px",
    background: "rgba(255,255,255,0.2)",
    padding: "8px 12px",
    borderRadius: "50%",
    transition: "background 0.2s",
    color: "#333",
  },
  title: {
    fontSize: "1.6rem",
    fontWeight: 700,
    marginBottom: 20,
    color: "#222",
  },
  query: {
    color: "var(--primary)",
  },
  skeletonWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
  },
  cardsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
  },
  productCard: {
    flex: "0 1 calc(50% - 6px)",
    minWidth: "calc(50% - 6px)",
    maxWidth: "calc(50% - 6px)",
    backgroundColor: "transparent",
    padding: 0,
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  productImageContainer: {
    position: "relative",
    height: "190px",
    overflow: "hidden",
    borderRadius: "18px",
    background: "#ededf0",
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  productInfo: {
    marginTop: "6px",
    marginBottom: "2px",
    padding: "0 0 10px",
    lineHeight: "1.3",
  },
  productName: {
    display: "block",
    width: "100%",
    fontSize: "13px",
    fontWeight: 500,
    color: "#18141f",
    margin: 0,
    lineHeight: "1.3",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left",
  },
  productMeta: {
    fontSize: "11px",
    color: "#8f899a",
    margin: "2px 0 4px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left",
  },
  priceContainer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  currentPrice: {
    fontSize: "15px",
    fontWeight: 800,
    color: "#1f1a28",
    marginLeft: 0,
  },
  originalPrice: {
    fontSize: "11px",
    marginLeft: 0,
    color: "#9b93ab",
    textDecoration: "line-through",
  },
  relatedTitle: {
    fontSize: "1.5rem",
    fontWeight: 700,
    marginTop: 32,
    marginBottom: 16,
  },
  noResults: {
    padding: 40,
    textAlign: "center",
    color: "#999",
    fontSize: 16,
  },
};

// Simple Skeleton Card Component
const SkeletonCard = () => (
  <div
    style={{
      ...styles.productCard,
      height: 252,
      borderRadius: 18,
      background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
      backgroundSize: "200% 100%",
      animation: "skeleton-loading 1.2s infinite linear",
    }}
  />
);

// Skeleton Animation
const skeletonStyle = `
  @keyframes skeleton-loading {
    0% { background-position: 200% 0 }
    100% { background-position: -200% 0 }
  }
`;

export default function SearchResults() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(search).get("q");
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  useEffect(() => {
    const search_query = async () => {
      try {
        const response = await api.get(`/api/search?q=${encodeURIComponent(query)}`);
        setResults(response.data.results || {});
      } catch (error) {
        console.error("Search error", error);
        setResults({});
      } finally {
        setLoading(false);
      }
    };
    if (query) search_query();
  }, [query]);

  // Check if there are any results
  const hasResults =
    (results.medicines && results.medicines.length > 0) ||
    ["doctors", "pharmacies", "labtests", "blogs", "providers"].some(
      (section) => results[section] && results[section].length > 0
    );

  const hasRelatedResults = ["doctors", "pharmacies", "blogs", "providers"].some(
    (section) => results[section] && results[section].length > 0
  );

  return (
    <div style={styles.page}>
      <style>{skeletonStyle}</style>

      {/* Back Button */}
      <div style={styles.backWrap}>
        <i
          className="fi fi-br-angle-left"
          onClick={() => navigate(-1)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={styles.backButton}
        ></i>
      </div>

      {/* Title */}
      <h2 style={styles.title}>
        Search Results for: <span style={styles.query}>{query}</span>
      </h2>

      {/* Loading Skeleton */}
      {loading ? (
        <div style={styles.skeletonWrap}>
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : !hasResults ? (
        // No results message
        <div style={styles.noResults}>
          No results found for <strong>{query}</strong>
        </div>
      ) : (
        <>
          {/* Medicines Results */}
          <div style={styles.cardsWrap}>
            {(results.medicines || []).map((item, index) => {
              const imageUrl = item.images && item.images.length > 0 ? item.images[0] : "";
              const hasOriginalPrice = Number(item.originalPrice) > Number(item.price);
              return (
                <div
                  key={index}
                  onClick={() => navigate(`/product/${item._id}`)}
                  style={styles.productCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={styles.productImageContainer}>
                    <img
                      src={imageUrl}
                      alt={item.name}
                      style={styles.productImage}
                    />
                  </div>
                  <div style={styles.productInfo}>
                    <h5 style={styles.productName}>{item.name}</h5>
                    <p style={styles.productMeta}>{item.category || "Medicine"}</p>
                    <div style={styles.priceContainer}>
                      {hasOriginalPrice && (
                        <span style={styles.originalPrice}>${item.originalPrice}</span>
                      )}
                      <span style={styles.currentPrice}>${item.price ?? "--"}</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {(results.labtests || []).map((item, index) => (
              <div
                key={`labtest-${index}`}
                onClick={() => navigate(`/lab-tests/${item._id}`)}
                style={styles.productCard}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div style={styles.productImageContainer}>
                  {item.image ? (
                    <img src={item.image} alt={item.name} style={styles.productImage} />
                  ) : (
                    <div
                      style={{
                        ...styles.productImage,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 42,
                      }}
                    >
                      🧪
                    </div>
                  )}
                </div>
                <div style={styles.productInfo}>
                  <h5 style={styles.productName}>{item.name}</h5>
                  <p style={styles.productMeta}>{item.category || "Lab Test"}</p>
                  <div style={styles.priceContainer}>
                    <span style={styles.currentPrice}>${item.price ?? "--"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Related Results */}
          {hasRelatedResults && (
            <h2 style={styles.relatedTitle}>
              Related Results for: <span style={styles.query}>{query}</span>
            </h2>
          )}

          {["doctors", "pharmacies", "blogs", "providers"].map(
            (section) =>
              results[section] && results[section].length > 0 && (
                <div key={section} style={{ marginBottom: 24 }}>
                  <h4 style={{ fontWeight: 700, marginBottom: 12 }}>
                    {section.charAt(0).toUpperCase() + section.slice(1)}
                  </h4>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {results[section].map((item, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          if (section === "doctors")
                            navigate(`/doctor-details/${item._id}`);
                          else if (section === "pharmacies")
                            navigate(`/pharmacy/${item._id}`);
                          else if (section === "providers")
                            navigate(`/providers/${item._id}`);
                          else if (section === "labtests")
                            navigate(`/lab-tests/${item._id}`);
                          else if (section === "blogs") navigate(`/blog/${item._id}`);
                        }}
                        style={{
                          background: "#fff",
                          borderRadius: 12,
                          padding: 12,
                          boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "transform 0.2s, box-shadow 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.12)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 3px 12px rgba(0,0,0,0.08)";
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <strong>{item.name || item.title || item._id}</strong>
                          <div style={{ fontSize: 12, color: "#666" }}>
                            {item.specialization || item.category || item.email || item.address || ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
          )}
        </>
      )}
    </div>
  );
}
